import { useState } from "react";
import { Star } from "lucide-react";
import { Reveal } from "../components/Reveal";
import VideoDialog from "../components/VideoDialog";
import VideoThumb from "../components/VideoThumb";
import { GOOGLE_BEWERTUNGEN, GOOGLE_STERNE } from "../lib/bewertungen";

const KENNZAHLEN = [
  { zahl: "4–5", text: "qualifizierte Entscheidergespräche pro Monat, mit denen wir planen. In der Regel liegen wir darüber." },
  { zahl: "500+", text: "Geschäftsführer, deren LinkedIn-Profile wir betreut haben" },
  { zahl: "45 Min.", text: "im Monat: so viel Zeit kostet Sie die Steuerung, sobald die Kampagne läuft" }
];

const GOOGLE_STIMMEN = [
  { zitat: "Mit 2besales die Kundenakquise outsourcen, über LinkedIn einfach genial: schnell, professionell und extrem effizient.", name: "Andreas Müllner" },
  { zitat: "Es hat mich beeindruckt, wie sich das Team in die fachspezifische Kommunikation eingefühlt hat. Der Response auf die Kampagne war überdurchschnittlich gut.", name: "Friedemann Tischmeyer" },
  { zitat: "Die Ergebnisse der Kampagne waren sehr positiv und haben selbst die größten Skeptiker in unserem Unternehmen überzeugt.", name: "FRT GmbH" },
  { zitat: "Starkes Verständnis für Vertrieb, Positionierung und Leadgenerierung. Klare Empfehlung für Unternehmen, die messbare Resultate suchen.", name: "Adrian Fopp" },
  { zitat: "Ein optimaler Weg, neue Geschäftskontakte zu knüpfen, ja sogar Geschäftsfelder auszuloten.", name: "Jakob Portenlänger" },
  { zitat: "Mein LinkedIn-Profil wird professionell betreut, von passendem Content bis zu relevanten Terminen.", name: "Sascha Alker" },
  { zitat: "Eigene Vorschläge zur Optimierung der Kampagne werden wie selbstverständlich gemacht. Eine Zusammenarbeit empfehle ich vorbehaltlos.", name: "Jochen Prinz" }
];

const REFERENZEN = [
  {
    initialen: "PG",
    name: "Peter Gorini",
    funktion: "Geschäftsführer",
    firma: "Global Service Management AG",
    vorher: "Sehr spitze, schwer erreichbare Zielgruppe im DACH-Raum, über klassische Wege kaum ansprechbar.",
    ergebnis: "45 hochqualifizierte Entscheidergespräche in vier Monaten.",
    zitat: "",
    video: "https://vimeo.com/1053332659"
  },
  {
    initialen: "MA",
    name: "Marcus Arens",
    funktion: "Country Managing Director",
    firma: "FI Group",
    vorher: "Neukunden kamen über Empfehlungen, nicht planbar.",
    ergebnis: "23 hochqualifizierte Entscheidergespräche, daraus 3 Neukunden.",
    zitat: "Jetzt kommen Neukunden über LinkedIn – mit minimalem Aufwand.",
    video: "https://vimeo.com/1053331388"
  },
  {
    initialen: "DS",
    name: "Daniel Schönbohm",
    funktion: "Geschäftsführer",
    firma: "MACEAS GmbH",
    vorher: "Maschinenbau mit erklärungsbedürftigem Produkt, Vertrieb hing an Messen und Bestandskunden.",
    ergebnis: "",
    zitat: "Der größte Vorteil: konstant neue Kunden, ohne täglich aktiv sein zu müssen.",
    video: "https://vimeo.com/reviews/2d3d90ca-f2d1-4f62-a24b-6c8b24e28743/videos/1053353837"
  }
];

const LOGOS = [
  { name: "Siemens", stil: "text-2xl font-bold tracking-[-0.02em]" },
  { name: "TÜV", stil: "text-2xl font-semibold" },
  { name: "Cornelsen", stil: "text-2xl font-medium tracking-[-0.01em]" },
  { name: "SAP", stil: "text-2xl font-bold tracking-[0.25em]" }
];

export default function Belege() {
  const [videoOffen, setVideoOffen] = useState(null);

  return (
    <section className="navy-depth py-20 sm:py-28 lg:py-32">
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <Reveal>
          <div className="flex items-center gap-4">
            <span className="h-px w-10 bg-hellblau/50" />
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-hellblau">Belege</p>
          </div>
          <h2 className="mt-6 text-2xl font-medium tracking-[-0.01em] text-white sm:text-3xl lg:text-4xl" data-testid="belege-ueberschrift">
            Was Sie erwarten können
          </h2>
        </Reveal>

        <div className="mt-12 grid gap-4 sm:grid-cols-3">
          {KENNZAHLEN.map((k, i) => (
            <Reveal key={k.zahl} delay={i * 0.05}>
              <div className="h-full rounded-[2px] border border-white/10 bg-white/[0.04] p-8 lg:p-10" data-testid={`kennzahl-${i + 1}`}>
                <p className="text-5xl font-light tracking-[-0.02em] text-gruen lg:text-6xl">{k.zahl}</p>
                <p className="mt-4 text-sm leading-[1.8] text-hellblau/90">{k.text}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-6 grid gap-4 lg:grid-cols-3">
          {REFERENZEN.map((r, i) => (
            <Reveal key={r.name} delay={i * 0.05}>
              <div className="flex h-full flex-col overflow-hidden rounded-[2px] bg-white p-8 lg:p-10" data-testid={`referenz-${i + 1}`}>
                <VideoThumb
                  videoUrl={r.video}
                  onClick={() => setVideoOffen(r.video)}
                  hinweis="Referenz-Video"
                  testid={`referenz-video-vorschau-${i + 1}`}
                  className="-mx-8 -mt-8 aspect-video lg:-mx-10 lg:-mt-10"
                />
                <div className="mt-6 flex items-center gap-4">
                  <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-navy/15 bg-hellblau/70 text-base font-semibold text-navy">
                    {r.initialen}
                  </div>
                  <div>
                    <p className="text-base font-semibold text-textdunkel">{r.name}</p>
                    <p className="mt-0.5 text-xs leading-relaxed text-textgrau">{r.funktion} · {r.firma}</p>
                  </div>
                </div>
                <div className="my-6 h-px bg-navy/10" />
                <p className="text-sm leading-[1.8] text-textgrau">
                  <span className="font-semibold">Vorher:</span> {r.vorher}
                </p>
                {r.ergebnis && (
                  <p className="mt-2 text-sm font-medium leading-[1.8] text-textdunkel">
                    <span className="font-semibold">Ergebnis:</span> {r.ergebnis}
                  </p>
                )}
                {r.zitat && <p className="mt-4 text-sm leading-[1.8] text-textgrau">„{r.zitat}“</p>}
                <div className="mt-auto pt-8">
                  <button
                    type="button"
                    data-testid={`referenz-video-${i + 1}`}
                    onClick={() => setVideoOffen(r.video)}
                    className="w-full rounded-[2px] border border-navy/25 px-6 py-3 text-xs font-semibold uppercase tracking-[0.12em] text-navy transition-all duration-300 hover:border-navy hover:bg-navy hover:text-white"
                  >
                    Video ansehen
                  </button>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <div className="mt-12" data-testid="google-bereich">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-hellblau">
                Stimmen unserer Kunden auf Google
              </p>
              <span className="flex items-center gap-2" data-testid="google-bewertungszeile">
                <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true" className="text-white/30">
                  <path fill="currentColor" d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47c-.29 1.48-1.14 2.73-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82z" />
                  <path fill="currentColor" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09C3.26 21.3 7.31 24 12 24z" />
                  <path fill="currentColor" d="M5.27 14.29c-.25-.72-.38-1.49-.38-2.29s.14-1.57.38-2.29V6.62H1.29C.47 8.24 0 10.06 0 12s.47 3.76 1.29 5.38l3.98-3.09z" />
                  <path fill="currentColor" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.31 0 3.26 2.7 1.29 6.62l3.98 3.09C6.22 6.86 8.87 4.75 12 4.75z" />
                </svg>
                <span className="text-xs text-white/70">{GOOGLE_STERNE} von 5 Sternen bei {GOOGLE_BEWERTUNGEN} Bewertungen</span>
              </span>
            </div>
          </div>
        </Reveal>

        <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {GOOGLE_STIMMEN.map((g, i) => (
            <Reveal key={g.name} delay={i * 0.04}>
              <div className="flex h-full flex-col rounded-[2px] border border-white/10 bg-white/[0.04] p-5" data-testid={`google-stimme-${i + 1}`}>
                <span className="flex gap-0.5 text-gruen">
                  {[0, 1, 2, 3, 4].map((s) => (
                    <Star key={s} size={11} fill="currentColor" strokeWidth={0} />
                  ))}
                </span>
                <p className="mt-3 text-xs leading-[1.7] text-hellblau/90">„{g.zitat}“</p>
                <p className="mt-3 text-[11px] text-hellblau/50">{g.name} · Google-Rezension</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <div className="mt-14 border-t border-white/10 pt-10" data-testid="logoleiste">
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-hellblau">
              Außerdem haben wir gearbeitet für
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-x-14 gap-y-5 text-white/40">
              {LOGOS.map((l) => (
                <span key={l.name} className={l.stil}>{l.name}</span>
              ))}
            </div>
          </div>
        </Reveal>

        <Reveal>
          <p className="mt-12 text-sm text-hellblau/85" data-testid="belege-referenzen-hinweis">
            Referenzkunden aus Ihrer Branche stellen wir Ihnen im Gespräch gerne persönlich vor.
          </p>
        </Reveal>
      </div>

      <VideoDialog offen={Boolean(videoOffen)} onClose={() => setVideoOffen(null)} titel="Referenz-Video" videoUrl={videoOffen || ""} />
    </section>
  );
}
